// ============================================================================
// CONSTANTS AND CONFIGURATION
// ============================================================================

const DEFAULT_CURRENCY = "EUR";
const DEFAULT_LOCALE = "sl-SI";
const CURRENCY_DECIMAL_PLACES = 2;

const SELECTORS = {
  widgetWrapper: ".lp-widget-wrapper",
  previewAmount: ".lp-preview-amount",
  openPopup: ".lp-open-popup",
};

const API_CONFIG = {
  proxyPath: "/apps/leanpay-installments/calculate",
  timeout: 10000,
};

const ERROR_MESSAGES = {
  fetchFailed: "Failed to fetch installment plans",
  noTrigger: "No trigger element provided",
  initFailed: "Failed to initialize installment widget",
};

const SAMPLE_INSTALLMENT_MONTHS = [6, 12, 24];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Find the plan with the cheapest installment amount
 * @param {Array} labeledPlans - Array of installment plans
 * @returns {Object|null} The plan with the lowest installment amount, or null if no plans
 */
function getCheapestPlan(labeledPlans) {
  if (
    !labeledPlans ||
    !Array.isArray(labeledPlans) ||
    labeledPlans.length === 0
  ) {
    return null;
  }

  return labeledPlans.reduce((cheapest, current) => {
    return current.installmentAmount < cheapest.installmentAmount
      ? current
      : cheapest;
  });
}

/**
 * Parse European/international number format and convert to proper float
 * @param {string} numberString - Number string in European format (e.g., "1.025,00")
 * @returns {number} Parsed number
 */
function parseEuropeanNumber(numberString) {
  if (!numberString || typeof numberString !== "string") {
    return 0;
  }

  // Remove any whitespace
  const cleaned = numberString.trim();

  // Handle European format: thousands separator is dot, decimal separator is comma
  // Examples: "1.025,00" -> 1025.00, "125,50" -> 125.50, "1.234.567,89" -> 1234567.89
  const lastCommaIndex = cleaned.lastIndexOf(",");
  const lastDotIndex = cleaned.lastIndexOf(".");

  if (lastCommaIndex > lastDotIndex) {
    // Comma is the decimal separator
    const integerPart = cleaned.substring(0, lastCommaIndex).replace(/\./g, "");
    const decimalPart = cleaned.substring(lastCommaIndex + 1);
    return parseFloat(integerPart + "." + decimalPart);
  } else if (lastDotIndex > lastCommaIndex) {
    const afterDot = cleaned.substring(lastDotIndex + 1);

    // If there are more than 3 digits after dot, it's likely thousands separator
    if (afterDot.length > 3) {
      return parseFloat(cleaned.replace(/\./g, ""));
    } else {
      // Normal decimal separator
      return parseFloat(cleaned.replace(/,/g, ""));
    }
  }

  // No decimal separator found, just remove any dots (thousands separators)
  return parseFloat(cleaned.replace(/\./g, ""));
}

/**
 * Check if we're in Shopify theme editor preview mode
 * @returns {boolean} True if in theme editor preview mode
 */
function isThemeEditorPreview() {
  return (
    typeof window !== "undefined" &&
    window.Shopify &&
    (window.Shopify.designMode === true ||
      window.Shopify.visualPreviewMode === true)
  );
}

// ============================================================================
// SAMPLE DATA FUNCTIONS
// ============================================================================

/**
 * Get sample installment data for theme editor preview
 * @param {number} productPrice - The product price
 * @returns {Object} Sample installment plans data
 */
function getSampleInstallmentData(productPrice) {
  return {
    loanAmount: productPrice,
    infoUrl: null, // No info URL for sample data
    labeledPlans: SAMPLE_INSTALLMENT_MONTHS.map((months) => ({
      numberOfMonths: months,
      installmentAmount: parseFloat(
        (productPrice / months).toFixed(CURRENCY_DECIMAL_PLACES),
      ),
      totalAmount: productPrice,
      label: `${months} ${window.leanpayTranslations?.installment_widget?.months || "months"}`,
    })),
  };
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Fetch installment plans from the proxy API
 * @param {number} price - Product price
 * @param {string} currency - Currency code
 * @returns {Promise<Object|null>} Installment plans data or null if failed
 */
async function fetchInstallmentPlans(price, currency = DEFAULT_CURRENCY) {
  const proxyUrl = `${API_CONFIG.proxyPath}?price=${price}&currency=${currency}`;

  try {
    const response = await fetch(proxyUrl);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.error || `HTTP error! status: ${response.status}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`[LEANPAY] ${ERROR_MESSAGES.fetchFailed}`, {
      error: error.message,
      url: proxyUrl,
    });
    return null;
  }
}

/**
 * Transform API response to labeled plans
 * @param {Object} apiResponse - Response from the proxy API
 * @returns {Object|null} Labeled plans data or null if invalid
 */
function transformApiResponse(apiResponse) {
  if (
    !apiResponse?.data?.available ||
    !apiResponse?.data?.installments?.length
  ) {
    return null;
  }

  return {
    loanAmount: apiResponse.data?.loanAmount,
    infoUrl: apiResponse.data?.infoUrl || null,
    labeledPlans: apiResponse.data?.installments.map((inst) => ({
      numberOfMonths: inst.months,
      installmentAmount: inst.monthlyAmount,
      totalAmount: inst.totalAmount,
      label: `${inst.months} ${window.leanpayTranslations?.installment_widget?.months || "months"}`,
    })),
  };
}

// ============================================================================
// POPUP CONTENT MANAGEMENT
// ============================================================================

/**
 * Create popup with current price from widget data
 * @param {HTMLElement} trigger - The element that triggered the popup
 */
async function createPopupWithCurrentPrice(trigger) {
  const widgetWrapper = trigger.closest(SELECTORS.widgetWrapper);
  if (!widgetWrapper) {
    console.error("[LEANPAY] No widget wrapper found");
    return;
  }

  // Get current price from widget data
  const currentPriceString =
    widgetWrapper.getAttribute("data-product-price") || "0";
  const currentPrice = parseEuropeanNumber(currentPriceString);
  const currency =
    widgetWrapper.getAttribute("data-currency-code") || DEFAULT_CURRENCY;
  const locale = widgetWrapper.getAttribute("data-locale") || DEFAULT_LOCALE;

  if (currentPrice == null || isNaN(currentPrice)) {
    console.error("[LEANPAY] Invalid current price:", currentPrice);
    return;
  }

  // Use cached data if available, otherwise fetch
  let transformedData = widgetWrapper._leanpayData;

  if (!transformedData) {
    // No cached data, need to fetch
    if (isThemeEditorPreview()) {
      transformedData = getSampleInstallmentData(currentPrice);
    } else {
      const plansData = await fetchInstallmentPlans(currentPrice, currency);
      if (!plansData) {
        console.error(
          "[LEANPAY] Failed to fetch plans for current price:",
          currentPrice,
        );
        return;
      }

      transformedData = transformApiResponse(plansData);
      if (!transformedData) {
        console.error("[LEANPAY] Failed to transform API response");
        return;
      }
    }

    // Cache it for next time
    widgetWrapper._leanpayData = transformedData;
  }

  // Create popup with data
  createPopup(trigger, transformedData, currentPrice, currency, locale);
}

/**
 * Create and show the installment popup
 * @param {HTMLElement} trigger - The element that triggered the popup
 * @param {Object} plansData - The transformed installment plans data from API
 * @param {number} productPrice - The product price
 * @param {string} currency - Currency code
 * @param {string} locale - Locale for formatting
 */
function createPopup(
  trigger,
  plansData,
  productPrice,
  currency = DEFAULT_CURRENCY,
  locale = DEFAULT_LOCALE,
) {
  if (!trigger) {
    console.error(`[LEANPAY] ${ERROR_MESSAGES.noTrigger}`);
    return;
  }
  if (
    window &&
    window.LeanpayPopup &&
    typeof window.LeanpayPopup.createPopup === "function"
  ) {
    window.LeanpayPopup.createPopup(
      trigger,
      plansData,
      productPrice,
      currency,
      locale,
    );
  } else {
    console.error("[LEANPAY] Popup module not loaded");
  }
}

// ============================================================================
// WIDGET INITIALIZATION
// ============================================================================

/**
 * Set up the installment widget on the product page
 * @param {HTMLElement} previewWidget - The widget wrapper element
 * @param {number} productPrice - The product price
 * @param {string} currency - Currency code
 */
async function initInstallmentWidget(
  previewWidget,
  productPrice,
  currency = DEFAULT_CURRENCY,
) {
  if (!previewWidget) {
    return;
  }

  // Ensure unique ID/dataset for this widget instance
  if (!previewWidget.dataset.lpWidgetId) {
    const uniqueId = `lp-widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    previewWidget.dataset.lpWidgetId = uniqueId;
    if (!previewWidget.id) {
      previewWidget.id = uniqueId;
    }
  }

  let transformedData;

  // Check if we're in theme editor preview mode
  if (isThemeEditorPreview()) {
    transformedData = getSampleInstallmentData(productPrice);
  } else {
    const plansData = await fetchInstallmentPlans(productPrice, currency);
    if (!plansData) {
      previewWidget.style.visibility = "hidden";
      previewWidget.style.display = "none";
      return;
    }

    // Transform API response to match our expected format
    transformedData = transformApiResponse(plansData);
    if (!transformedData) {
      previewWidget.style.visibility = "hidden";
      previewWidget.style.display = "none";
      return;
    }
  }

  // Cache the transformed data on the widget
  previewWidget._leanpayData = transformedData;

  // Update preview amount
  const previewAmountEl = previewWidget.querySelector(SELECTORS.previewAmount);
  const cheapestPlan = getCheapestPlan(transformedData.labeledPlans);
  if (previewAmountEl && cheapestPlan) {
    previewAmountEl.textContent = cheapestPlan.installmentAmount.toFixed(
      CURRENCY_DECIMAL_PLACES,
    );
  }

  // Set up event listeners
  const triggers = [previewWidget.querySelector(SELECTORS.openPopup)].filter(
    Boolean,
  );

  triggers.forEach((trigger) => {
    trigger.addEventListener("click", async (e) => {
      e.preventDefault();
      await createPopupWithCurrentPrice(trigger);
    });
  });

  // Show widget with a smooth transition only after everything is set up
  previewWidget.style.visibility = "visible";
  previewWidget.style.opacity = "1";
  previewWidget.style.transition = "opacity 0.3s ease-in-out";
}

// ============================================================================
// DYNAMIC PRICE UPDATE HANDLING
// ============================================================================

/**
 * Update widget with new price data
 * @param {HTMLElement} widgetWrapper - The widget wrapper element
 * @param {number} newPrice - New product price
 * @param {string} currency - Currency code
 * @param {string} locale - Locale for formatting
 */
async function updateWidgetPrice(widgetWrapper, newPrice, currency, locale) {
  if (!widgetWrapper) return;

  if (newPrice == null || isNaN(newPrice)) {
    console.error("[LEANPAY] Invalid price for widget update:", newPrice);
    widgetWrapper.style.visibility = "hidden";
    widgetWrapper.style.display = "none";
    return;
  }

  // Update the data attribute
  widgetWrapper.setAttribute("data-product-price", newPrice.toString());

  let transformedData;

  // Check if we're in theme editor preview mode
  if (isThemeEditorPreview()) {
    transformedData = getSampleInstallmentData(newPrice);
  } else {
    const plansData = await fetchInstallmentPlans(newPrice, currency);
    if (!plansData) {
      widgetWrapper.style.visibility = "hidden";
      widgetWrapper.style.display = "none";
      return;
    }

    // Transform API response to match our expected format
    transformedData = transformApiResponse(plansData);
    if (!transformedData) {
      widgetWrapper.style.visibility = "hidden";
      widgetWrapper.style.display = "none";
      return;
    }
  }

  // Cache the transformed data on the widget
  widgetWrapper._leanpayData = transformedData;

  // Update preview amount
  const previewAmountEl = widgetWrapper.querySelector(SELECTORS.previewAmount);
  const cheapestPlan = getCheapestPlan(transformedData.labeledPlans);
  if (previewAmountEl && cheapestPlan) {
    previewAmountEl.textContent = cheapestPlan.installmentAmount.toFixed(
      CURRENCY_DECIMAL_PLACES,
    );
  }

  // Show widget if it was hidden
  widgetWrapper.style.display = "";
  widgetWrapper.style.visibility = "visible";
  widgetWrapper.style.opacity = "1";

  // Close any open popup for this widget - it will recreate when clicked
  const widgetPopup = widgetWrapper.querySelector(".lp-popup-overlay");
  if (widgetPopup) {
    widgetPopup.remove();
  }
}

/**
 * Handle price change events from theme
 * @param {CustomEvent} event - The price change event
 */
async function handlePriceChange(event) {
  const { price, currency } = event.detail;

  if (price == null || typeof price !== "number") {
    console.warn("[LEANPAY] Invalid price in price change event:", price);
    return;
  }

  const widgetWrappers = document.querySelectorAll(SELECTORS.widgetWrapper);

  for (const widgetWrapper of widgetWrappers) {
    const widgetCurrency =
      currency ||
      widgetWrapper.getAttribute("data-currency-code") ||
      DEFAULT_CURRENCY;
    const locale = widgetWrapper.getAttribute("data-locale") || DEFAULT_LOCALE;

    try {
      await updateWidgetPrice(widgetWrapper, price, widgetCurrency, locale);
    } catch (error) {
      console.error("[LEANPAY] Error updating widget price:", error);
    }
  }
}

// ============================================================================
// MAIN INITIALIZATION
// ============================================================================

/**
 * Initialize all installment widgets on the page
 */
async function initializeAllWidgets() {
  const widgetWrappers = document.querySelectorAll(SELECTORS.widgetWrapper);
  if (!widgetWrappers.length) return;

  for (const widgetWrapper of widgetWrappers) {
    const productPriceString =
      widgetWrapper.getAttribute("data-product-price") || "0";
    const productPrice = parseEuropeanNumber(productPriceString);
    const currency =
      widgetWrapper.getAttribute("data-currency-code") || DEFAULT_CURRENCY;

    if (productPrice != null && !isNaN(productPrice)) {
      try {
        await initInstallmentWidget(widgetWrapper, productPrice, currency);
      } catch (error) {
        console.error(`[LEANPAY] ${ERROR_MESSAGES.initFailed}`, error);
        widgetWrapper.style.visibility = "hidden";
        widgetWrapper.style.display = "none";
      }
    } else {
      widgetWrapper.style.visibility = "hidden";
      widgetWrapper.style.display = "none";
    }
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", initializeAllWidgets);

// Listen for price change events
window.addEventListener("leanpay:priceChange", handlePriceChange);